import { AccountStatus } from '@enums/account/AccountStatus';
import { Platform } from '@enums/account/Platform';
import { IAccount } from '@services/account.service';
import { CurrentUserService } from '@services/currentUser.service';
import { DisplayService, IDialogOptionsJson } from '@services/display.service';
import { TranslationService } from '@services/translation.service';
import { IReseller } from '../_models/IReseller';

export interface IAccountSummary {
    ManualNoteCount: number;

    AutomaticNoteCount: number;

    TicketCount: number;

    VehicleCount: number;

    JobCount: number;

    CommissionCount: number;

    UserCount: number;
}

export class FoundationAccountService {

    static $inject = ["$q", "$http", "$mdDialog", "displayService", "currentUserService", "translationService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly displayService: DisplayService,
        private readonly currentUserService: CurrentUserService,
        private readonly translationService: TranslationService
    ) {
    }

    getCustomerAccounts(invalidateCache = false): angular.IPromise<IAccount[]> {
        return this.$http<IAccount[]>({
            url: `/api/foundation/accounts/`,
            method: "GET",
            cache: true,
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        }).then(response => {
            // For now lets hide any none active accounts
            return response.data.filter(a => a.Status != AccountStatus.Prospective);
        });
    }

    getCustomerAccount(accountId: number, invalidateCache = false): angular.IPromise<IAccount> {
        return this.getCustomerAccounts(invalidateCache).then(accounts => {
            return accounts.find(a => a.Id == accountId);
        });
    }

    getCustomerAccountsExtended(): angular.IPromise<IAccount[]> {
        return this.$http<IAccount[]>({
            url: `/api/foundation/accounts/extendedinfo/`,
            method: "GET",
            cache: true
        }).then(response => {
            return response.data;
        });
    }

    getCustomerAccountImage(accountId: number, invalidateCache = false): angular.IPromise<string> {
        return this.$http<string>({
            url: `/api/foundation/accounts/${accountId}/image`,
            method: "GET",
            cache: true,
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        }).then(response => {
            return response.data;
        });
    }

    getAccountSummary(accountId: number): angular.IPromise<IAccountSummary> {
        //return this.getCustomerAccount(accountId)
        //    .then(accountSummary => {
        //        return {
        //            commissionCount: 1234,
        //            jobCount: 1234,
        //            noteCount: 1234,
        //            ticketCount: 1234,
        //            vehicleCount: 1234
        //        };
        //    });
        return this.$http<IAccountSummary>({
            url: `/api/foundation/accounts/${accountId}/summary`,
            method: "GET",
            cache: true,
            params: { minsToLive: 30 }
        }).then(response => {

            return response.data;
        });
    }

    updateCustomerFeatures(accountId: number): angular.IPromise<any> {
        return this
            .$http({
                method: "POST",
                url: "/api/foundation/accounts/updatefeatures",
                data: accountId
            });
    }

    save(account: IAccount): angular.IPromise<number> {
        if (!account) {
            return this.$q.reject("No account provided");
        }

        // Validate model

        account.Website = account.Website || "";
        account.Platform = account.Platform || Platform.Verilocation;

        return this
            .$http<number>({
                method: "POST",
                url: "/api/foundation/accounts/",
                data: account
            }).then(response => {
                return response.data;
            });
    }

    getResellers(): angular.IPromise<IReseller[]> {
        return this
            .$http<IReseller[]>({
                url: `/api/foundation/accounts/resellers`,
                method: "GET",
                cache: true,
                params: { minsToLive: 30 }
            })
            .then(response => {
                return response.data;
            })
            .catch(() => {
                return [];
            });
    }

    getResellerName(resellers: IReseller[], resellerId: number): string {
        if (resellerId) {
            var resellerAccount = resellers.find(r => r.Id == resellerId);
            return resellerAccount?.Description || "Unknown";
        }

        return `AddSecure UK`;
    }

    createAccountMap(accounts: IAccount[]): Map<number, IAccount> {
        return new Map(accounts.map(a => [a.Id, a]));
    }


    showCustomerImageDialog(event?: MouseEvent, accountId?: number) {
        return this.$mdDialog.show({
            template: require("../../../components/companyImage/companyImage.html"),
            targetEvent: event,
            multiple: true,
            escapeToClose: true,
            clickOutsideToClose: true,
            controller: "companyImageController",
            controllerAs: "$ctrl",
            locals: { accountId: accountId }
        });
    }

    showEditAccountDialog($event: MouseEvent, account: IAccount = null) {

        const controller = ($mdDialog: angular.material.IDialogService) => {
            return {
                hide: (requiresUpdate: boolean) => { $mdDialog.hide(requiresUpdate); },
                selectedAccount: account,
            }
        };

        controller.$inject = ["$mdDialog"];

        const editTitle = `${this.translationService.t("Common.Edit")}: ${account?.CompanyName}`;
        const addTitle = this.translationService.t("Foundation.AddCustomer");

        const dialogTitle = (account) ? editTitle : addTitle;
        var dialogHTML = `<foundation-customer-edit flex customer="$ctrl.selectedAccount" on-save="$ctrl.hide(true)"></foundation-customer-edit>`;

        const options: IDialogOptionsJson = {
            title: dialogTitle,
            dialogHTML: dialogHTML,
            dialogId: "editFoundationAccountDialog"
        };

        const dialogOptions: angular.material.IDialogOptions = {
            targetEvent: $event,
            escapeToClose: true,
            clickOutsideToClose: true,
            multiple: true,
            template: this.displayService.createDialogWrapper(options),
            controller: controller,
            controllerAs: "$ctrl"
        };

        return this.$mdDialog.show(dialogOptions).then((requiresUpdate: boolean) => {
            return requiresUpdate;
        });
    }
}
